import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { signOut } from '../store/slices/authSlice'
import { colors } from '../constants/colors'
import Sidebar from '../components/(admin)/Sidebar'
import PageTopBar from '../components/(admin)/PageTopBar'
import deleteIcon from '../assets/images/delete.png'
import { 
  fetchKBSources,
  deleteKBSource,
  uploadKBFile,
  clearError,
  clearSuccess
} from '../store/slices/kbSlice'
import NotificationModal from '../components/NotificationModal'

const AITrainingData = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { user } = useSelector((state) => state.auth)
  const { kbSources, loading: isLoading, uploading: isUploading, error, success } = useSelector((state) => state.kb)
  
  // Modal state
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [fileName, setFileName] = useState('')
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [uploadType, setUploadType] = useState('file') // 'file' or 'url'
  const [url, setUrl] = useState('')
  
  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [itemToDelete, setItemToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  
  // Data state
  const [trainingData, setTrainingData] = useState([])
  
  // Notification modal state
  const [notification, setNotification] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    showCancel: false,
    onConfirm: null
  })

  const [isSigningOut, setIsSigningOut] = useState(false)

  // Fetch KB sources on component mount
  useEffect(() => {
    dispatch(fetchKBSources())
  }, [dispatch])

  // Transform KB sources to training data format
  useEffect(() => {
    if (kbSources && kbSources.length > 0) {
      const transformedData = kbSources.map((item, index) => ({
        id: item._id || index,
        fileName: item.upload_metadata?.file_name || item.name || 'Unknown',
        type: (item.type === 'url' ? 'URL' : null) ||
              item.upload_metadata?.file_type?.split('/')[1]?.toUpperCase() ||
              item.upload_metadata?.file_name?.split('.').pop()?.toUpperCase() ||
              'Unknown',
        status: item.status ? item.status.charAt(0).toUpperCase() + item.status.slice(1) : 'Unknown',
        uploadedBy: 'Admin',
        uploadedOn: (() => {
          const d = item.created_at || item.createdAt || item.updated_at || item.updatedAt
          if (!d) return '—'
          const date = new Date(d)
          return isNaN(date.getTime()) ? '—' : date.toLocaleDateString('en-US', {
            month: 'numeric',
            day: 'numeric',
            year: '2-digit'
          })
        })()
      }))
      setTrainingData(transformedData)
    } else {
      setTrainingData([])
    }
  }, [kbSources])

  // Show notifications for errors and success
  useEffect(() => {
    if (error) {
      showNotification('error', 'Error', error)
      dispatch(clearError())
    }
    if (success) {
      showNotification('success', 'Success', success)
      dispatch(clearSuccess())
      // Refresh data after successful operations
      dispatch(fetchKBSources())
    }
  }, [error, success, dispatch])

  // Helper functions for notifications
  const showNotification = (type, title, message, showCancel = false, onConfirm = null) => {
    setNotification({
      isOpen: true,
      type,
      title,
      message,
      showCancel,
      onConfirm
    })
  }

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, isOpen: false }))
  }

  const handleDeleteClick = (item) => {
    setItemToDelete(item)
    setIsDeleteModalOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return

    setIsDeleting(true)
    dispatch(deleteKBSource(itemToDelete.id)).then(() => {
      setIsDeleteModalOpen(false)
      setItemToDelete(null)
      setIsDeleting(false)
    }).catch(() => {
      setIsDeleting(false)
    })
  }

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false)
    setItemToDelete(null)
  }

  const handleUploadClick = () => {
    setIsUploadModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsUploadModalOpen(false)
    setFileName('')
    setUploadedFiles([])
    setUploadType('file')
    setUrl('')
  }

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files)
    setUploadedFiles(files)
  }

  const handleDragOver = (event) => {
    event.preventDefault()
  }

  const handleDrop = (event) => {
    event.preventDefault()
    const files = Array.from(event.dataTransfer.files)
    setUploadedFiles(files)
  }

  const handleUploadDocument = async () => {
    if (!fileName.trim()) {
      showNotification('warning', 'Validation Error', 'Please enter a file name (intent name)')
      return
    }

    if (uploadType === 'file' && uploadedFiles.length === 0) {
      showNotification('warning', 'Validation Error', 'Please select at least one file to upload')
      return
    }

    if (uploadType === 'url' && !url.trim()) {
      showNotification('warning', 'Validation Error', 'Please enter a URL')
      return
    }

    let uploadData
    if (uploadType === 'file') {
      // Create FormData for file upload
      const formData = new FormData()
      formData.append('name', fileName.trim())
      
      // Add all selected files
      uploadedFiles.forEach((file, index) => {
        formData.append('file', file)
      })
      uploadData = formData
    } else {
      // Create JSON object for URL upload
      uploadData = {
        name: fileName.trim(),
        url: url.trim()
      }
    }

    dispatch(uploadKBFile(uploadData)).then(() => {
      handleCloseModal()
    })
  }

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'processing':
        return '#3b82f6' // Blue
      case 'failed':
        return '#ef4444' // Red
      case 'ready':
        return '#10b981' // Green
      case 'pending':
        return '#f59e0b' // Orange
      default:
        return colors.text.secondary
    }
  }

  const handleSignOut = async () => {
    setIsSigningOut(true)
    try {
      await dispatch(signOut()).unwrap()
    } catch (error) {
      console.error('Logout failed:', error)
    } finally {
      setTimeout(() => {
        navigate('/signin')
        setIsSigningOut(false)
      }, 500)
    }
  }

  const containerStyle = {
    minHeight: '100vh',
    backgroundColor: colors.background.secondary,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    display: 'flex'
  }

  const mainContentStyle = {
    flex: 1,
    marginLeft: '280px',
    padding: '24px',
    backgroundColor: colors.background.secondary
  }

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px'
  }

  const headerRightStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  }

  const headerIconStyle = {
    width: '24px',
    height: '24px',
    cursor: 'pointer',
    color: colors.text.secondary
  }

  const notificationIconStyle = {
    ...headerIconStyle,
    position: 'relative'
  }

  const notificationDotStyle = {
    position: 'absolute',
    top: '-2px',
    right: '-2px',
    width: '8px',
    height: '8px',
    backgroundColor: '#ef4444',
    borderRadius: '50%'
  }

  const uploadButtonStyle = {
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '500',
    color: colors.background.primary,
    backgroundColor: colors.primary,
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease'
  }

  const titleStyle = {
    fontSize: '28px',
    fontWeight: '700',
    color: colors.text.primary,
    margin: 0,
    marginBottom: '24px'
  }

  const tableContainerStyle = {
    backgroundColor: colors.background.primary,
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden'
  }

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse'
  }

  const headerRowStyle = {
    backgroundColor: '#f8fafc',
    borderBottom: `1px solid ${colors.borderVariants.light}`
  }

  const headerCellStyle = {
    padding: '16px 20px',
    textAlign: 'left',
    fontSize: '14px',
    fontWeight: '600',
    color: colors.text.primary,
    borderBottom: `1px solid ${colors.borderVariants.light}`
  }

  const rowStyle = {
    borderBottom: `1px solid ${colors.borderVariants.light}`,
    transition: 'background-color 0.2s ease'
  }

  const cellStyle = {
    padding: '16px 20px',
    fontSize: '14px',
    color: colors.text.primary,
    borderBottom: `1px solid ${colors.borderVariants.light}`
  }

  const statusStyle = {
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '500',
    display: 'inline-block'
  }

  const actionCellStyle = {
    ...cellStyle,
    display: 'flex',
    gap: '8px',
    alignItems: 'center'
  }

  const actionIconStyle = {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
    opacity: 0.6,
    transition: 'opacity 0.2s ease'
  }

  // Modal styles
  const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  }

  const modalStyle = {
    backgroundColor: colors.background.primary,
    borderRadius: '12px',
    padding: '32px',
    width: '500px',
    maxWidth: '90vw',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
  }

  const modalTitleStyle = {
    fontSize: '24px',
    fontWeight: '700',
    color: colors.text.primary,
    margin: '0 0 24px 0'
  }

  const inputGroupStyle = {
    marginBottom: '24px'
  }

  const labelStyle = {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: '8px'
  }

  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    fontSize: '14px',
    border: `1px solid ${colors.borderVariants.light}`,
    borderRadius: '8px',
    backgroundColor: colors.background.primary,
    color: colors.text.primary,
    outline: 'none',
    transition: 'border-color 0.2s ease',
    boxSizing: 'border-box'
  }

  const uploadAreaStyle = {
    border: '2px dashed #007AFF',
    borderRadius: '8px',
    padding: '40px 20px',
    textAlign: 'center',
    backgroundColor: '#f0f9ff',
    position: 'relative',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease'
  }

  const uploadIconStyle = {
    color: '#3b82f6',
    marginBottom: '16px'
  }

  const uploadTextStyle = {
    fontSize: '16px',
    color: colors.text.primary,
    margin: '0 0 16px 0'
  }

  const browseButtonStyle = {
    backgroundColor: '#007AFF',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease'
  }

  const dropdownIconStyle = {
    position: 'absolute',
    top: '12px',
    right: '12px',
    color: colors.text.secondary,
    cursor: 'pointer'
  }

  const buttonGroupStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '32px'
  }

  const cancelButtonStyle = {
    backgroundColor: 'white',
    color: colors.text.primary,
    border: `1px solid ${colors.borderVariants.light}`,
    borderRadius: '6px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease'
  }

  const uploadDocumentButtonStyle = {
    backgroundColor: '#007AFF',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease'
  }

  // Loading, error, and empty state styles
  const loadingContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    textAlign: 'center'
  }

  const spinnerStyle = {
    width: '40px',
    height: '40px',
    border: '4px solid #e2e8f0',
    borderTop: `4px solid ${colors.primary}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '16px'
  }

  const loadingTextStyle = {
    fontSize: '16px',
    color: colors.text.secondary,
    margin: 0
  }

  const errorContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    textAlign: 'center'
  }

  const errorTextStyle = {
    fontSize: '16px',
    color: colors.status.error,
    margin: '0 0 16px 0'
  }

  const retryButtonStyle = {
    backgroundColor: colors.primary,
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease'
  }

  const emptyContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    textAlign: 'center'
  }

  const emptyTextStyle = {
    fontSize: '16px',
    color: colors.text.secondary,
    margin: 0
  }

  // Delete modal styles
  const deleteModalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1001
  }

  const deleteModalStyle = {
    backgroundColor: colors.background.primary,
    borderRadius: '12px',
    padding: '32px',
    width: '400px',
    maxWidth: '90vw',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
  }

  const deleteModalTitleStyle = {
    fontSize: '20px',
    fontWeight: '700',
    color: colors.text.primary,
    margin: '0 0 16px 0'
  }

  const deleteModalTextStyle = {
    fontSize: '16px',
    color: colors.text.secondary,
    margin: '0 0 24px 0',
    lineHeight: '1.5'
  }

  const deleteModalButtonGroupStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px'
  }

  const deleteCancelButtonStyle = {
    backgroundColor: 'white',
    color: colors.text.primary,
    border: `1px solid ${colors.borderVariants.light}`,
    borderRadius: '6px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease'
  }

  const getDeleteConfirmButtonStyle = () => ({
    backgroundColor: '#ef4444',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    opacity: isDeleting ? 0.7 : 1
  })

  return (
    <div style={containerStyle}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      
      {/* Sidebar */}
      <Sidebar />
      
      {/* Main Content */}
      <div style={mainContentStyle}>
        {/* Header */}
        <PageTopBar
          title="AI Training Data"
          onAiConfig={() => navigate('/ai-configuration')}
          onEmbeddingGuide={() => navigate('/embedding-guide')}
          onSignOut={handleSignOut}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '24px' }}>
          <button style={uploadButtonStyle} onClick={handleUploadClick}>+ Upload File</button>
        </div>

        {/* Data Table */}
        <div style={tableContainerStyle}>
          {isLoading ? (
            <div style={loadingContainerStyle}>
              <div style={spinnerStyle}></div>
              <p style={loadingTextStyle}>Loading training data...</p>
            </div>
          ) : error ? (
            <div style={errorContainerStyle}>
              <p style={errorTextStyle}>Error: {error}</p>
              <button style={retryButtonStyle} onClick={() => dispatch(fetchKBSources())}>
                Retry
              </button>
            </div>
          ) : trainingData.length === 0 ? (
            <div style={emptyContainerStyle}>
              <p style={emptyTextStyle}>No training data found. Upload your first file to get started!</p>
            </div>
          ) : (
            <table style={tableStyle}>
              <thead style={headerRowStyle}>
                <tr>
                  <th style={headerCellStyle}>File Name</th>
                  <th style={headerCellStyle}>Type</th>
                  <th style={headerCellStyle}>Status</th>
                  <th style={headerCellStyle}>Uploaded By</th>
                  <th style={headerCellStyle}>Uploaded On</th>
                  <th style={headerCellStyle}>Action</th>
                </tr>
              </thead>
              <tbody>
                {trainingData.map((item) => (
                  <tr 
                    key={item.id} 
                    style={rowStyle}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8fafc'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent'
                    }}
                  >
                    <td style={cellStyle}>{item.fileName}</td>
                    <td style={cellStyle}>{item.type}</td>
                    <td style={cellStyle}>
                      <span 
                        style={{
                          ...statusStyle,
                          backgroundColor: getStatusColor(item.status) + '20',
                          color: getStatusColor(item.status)
                        }}
                      >
                        {item.status}
                      </span>
                    </td>
                    <td style={cellStyle}>{item.uploadedBy}</td>
                    <td style={cellStyle}>{item.uploadedOn}</td>
                    <td style={actionCellStyle}>
                      <img 
                        src={deleteIcon} 
                        alt="Delete" 
                        style={actionIconStyle}
                        onClick={() => handleDeleteClick(item)}
                        onMouseEnter={(e) => {
                          e.target.style.opacity = '1'
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.opacity = '0.6'
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      {isUploadModalOpen && (
        <div style={modalOverlayStyle}>
          <div style={modalStyle}>
            {/* Modal Header */}
            <h2 style={modalTitleStyle}>Upload Training Data</h2>
            
            {/* File Name Input */}
            <div style={inputGroupStyle}>
              <label style={labelStyle}>File Name</label>
              <input
                type="text"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                placeholder="Intent Name"
                style={inputStyle}
              />
            </div>
            
            {/* Upload Type Dropdown */}
            <div style={inputGroupStyle}>
              <label style={labelStyle}>Upload Type</label>
              <select
                value={uploadType}
                onChange={(e) => {
                  setUploadType(e.target.value)
                  setUploadedFiles([])
                  setUrl('')
                }}
                style={inputStyle}
              >
                <option value="file">Upload File</option>
                <option value="url">Upload URL</option>
              </select>
            </div>
            
            {/* Upload Files Section */}
            {uploadType === 'file' ? (
              <div style={inputGroupStyle}>
                <label style={labelStyle}>Upload Files</label>
                <div
                  style={uploadAreaStyle}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                >
                  {uploadedFiles.length > 0 ? (
                    <>
                      <p style={uploadTextStyle}>
                        {uploadedFiles.map(f => f.name).join(', ')}
                      </p>
                      {isUploading && <p style={{ ...uploadTextStyle, marginTop: 8 }}>Uploading...</p>}
                      <button style={browseButtonStyle} onClick={() => document.getElementById('fileInput').click()}>
                        Change Files
                      </button>
                    </>
                  ) : (
                    <>
                      <div style={uploadIconStyle}>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                        </svg>
                      </div>
                      <p style={uploadTextStyle}>Drag and Drop Files here or</p>
                      <button style={browseButtonStyle} onClick={() => document.getElementById('fileInput').click()}>
                        Browse Files
                      </button>
                    </>
                  )}
                  <input
                    id="fileInput"
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                  />
                </div>
              </div>
            ) : (
              <div style={inputGroupStyle}>
                <label style={labelStyle}>URL</label>
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com/page"
                  style={inputStyle}
                />
              </div>
            )}
            
            {/* Action Buttons */}
            <div style={buttonGroupStyle}>
              <button style={cancelButtonStyle} onClick={handleCloseModal}>
                Cancel
              </button>
              <button 
                style={{
                  ...uploadDocumentButtonStyle,
                  opacity: isUploading ? 0.7 : 1,
                  cursor: isUploading ? 'not-allowed' : 'pointer'
                }} 
                onClick={handleUploadDocument}
                disabled={isUploading}
              >
                {isUploading ? 'Uploading...' : 'Upload Document'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && itemToDelete && (
        <div style={deleteModalOverlayStyle}>
          <div style={deleteModalStyle}>
            <h2 style={deleteModalTitleStyle}>Delete Training Data</h2>
            <p style={deleteModalTextStyle}>
              Are you sure you want to delete <strong>"{itemToDelete.fileName}"</strong>? 
              This action cannot be undone and will remove all associated data from the knowledge base.
            </p>
            <div style={deleteModalButtonGroupStyle}>
              <button 
                style={deleteCancelButtonStyle} 
                onClick={handleDeleteCancel}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button 
                style={getDeleteConfirmButtonStyle()} 
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      <NotificationModal
        isOpen={notification.isOpen}
        onClose={closeNotification}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        showCancel={notification.showCancel}
        onConfirm={notification.onConfirm}
      />
    </div>
  )
}

export default AITrainingData
